import React, { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import searchMetadata from '@/data/searchMetadata.json';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useRegionStore from '../context/RegionStore';
import Button from '@/components/Button';
import { prefetchProductCollection } from '@/services/api'
import { HERO_IMAGES, type HeroImage } from '@/data/heroImageConfig';
import { publicationReviewStats, publicationReviews } from '../modules/reviews/data/publicationReviews';
import { sustainabilitySpotlight, buildSpotlight, madeInBritain } from '../modules/spotlights/data/spotlightExamples';
import { photosWithText, photosWithoutText } from '@/modules/photos/data/photoExamples';
import { galleryImages } from '@/modules/scrolling-gallery/data/galleryExamples';
import HeroCarousel from '@/components/HeroCarousel';
import HeroCTA from '@/components/HeroCTA';
import ScallopedShowcase from '@/components/ScallopedShowcase';
import BlackFridayPromo from '@/components/BlackFridayPromo';
import WelcomeVideo from '@/components/WelcomeVideo';
import LazyModule from '@/components/LazyModule';
import { getRegionFromUrl } from '@/utils/regionDetection';
import { getRegionalHomePath, SEO_REGIONS, toAbsoluteSiteUrl } from '@/utils/seoUrls';

// Lazy load non-critical components
// const ImageSlideshow = lazy(() => import('../components/ImageSlideshow'));
// const ProductDetailsCard = lazy(() => import('../components/ProductDetailsCard'));
const ReviewsModule = lazy(() => import('../modules/reviews/components/ReviewsModule'));
const UserGallery = lazy(() => import('@/components/UserGallery/UserGallery'));

const SpotlightsModule = lazy(() => import('../modules/spotlights/components/SpotlightModule'));
// const BannerModule = lazy(() => import('@/modules/banner/components/BannerModule'));
// const PhotoGridModule = lazy(() => import('@/modules/photos/components/PhotoGridModule'));
const ProductCollectionsModule = lazy(() => import('@/modules/collections/components/ProductCollectionsModule'));
const ComparisonModule = lazy(() => import('@/modules/comparisons/components/ComparisonModule'));
const KibuStoryModule = lazy(() => import('@/modules/kibu-story/components/KibuStoryModule'));
// const ScrollingGallery = lazy(() => import('@/modules/scrolling-gallery/components/ScrollingGallery'));
const BlogModule = lazy(() => import('@/modules/blog/components/BlogModule'));
const PublicationLogoBanner = lazy(() => import('@/modules/publication-logos/components/PublicationLogoBanner'));
const CompatibilityModule = lazy(() => import('@/modules/compatibility/components/CompatibilityModule'));
const EnquiriesModule = lazy(() => import('@/modules/forms/components/EnquiriesModule'));
const AwardsShowcaseModule = lazy(() => import('@/modules/awards/components/AwardsShowcaseModule'));

type ButtonVariant =
    | 'solid-white'
    | 'solid-black'
    | 'solid-yellow'
    | 'solid-butter'
    | 'solid-midnight'
    | 'solid-mudpie'
    | 'solid-red'
    | 'border-white'
    | 'border-black';

const noop = () => {};

const Home: React.FC = () => {
    const { chosenRegion, detectedRegion } = useRegionStore();
    const location = useLocation();
    const urlRegion = getRegionFromUrl(location.pathname);
    const region = (urlRegion || chosenRegion || 'gb').toLowerCase();
    const productCollectionRegion = (urlRegion || chosenRegion || detectedRegion || 'gb').toLowerCase();
    const canonicalUrl = urlRegion
        ? toAbsoluteSiteUrl(getRegionalHomePath(urlRegion))
        : toAbsoluteSiteUrl('/');
    const [activeHeroIndex, setActiveHeroIndex] = React.useState(0);
    const [snowCount, setSnowCount] = React.useState(80);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);

    // Use hero images from shared config (src/data/heroImageConfig.ts)
    // This ensures Home.tsx and the preload script in index.html stay in sync
    // Filter images based on current date and startDate/endDate
    const heroImages = React.useMemo(() => {
        const now = new Date();
        return HERO_IMAGES.filter(image => {
            // If no dates specified, always show
            if (!image.startDate && !image.endDate) return true;

            const startDate = image.startDate ? new Date(image.startDate) : null;
            const endDate = image.endDate ? new Date(image.endDate) : null;

            // Check if current date is within range
            if (startDate && now < startDate) return false;
            if (endDate && now > endDate) return false;

            return true;
        });
    }, []);

    const currentImage = heroImages[activeHeroIndex];

    const handleImageChange = (_imageData: any, index: number) => {
        setActiveHeroIndex(index);
    };

    const addMoreSnow = () => {
        setSnowCount(800); // Dramatically increase snow to 800
        setIsButtonDisabled(true);

        // After 5 seconds, fade out the extra snow gradually then reset
        setTimeout(() => {
            setSnowCount(prev => {
                // Gradually reduce snow from current level to 80
                const interval = setInterval(() => {
                    setSnowCount(current => {
                        const newCount = Math.max(current - 40, 80);
                        if (newCount === 80) {
                            clearInterval(interval);
                            setIsButtonDisabled(false);
                        }
                        return newCount;
                    });
                }, 100);
                return prev;
            });
        }, 5000);
    };

    React.useEffect(() => {
        prefetchProductCollection({
            collectionCode: 'F39Jxkd4q',
            country_code: productCollectionRegion,
        })
    }, [productCollectionRegion])

    const jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'Organization',
                '@id': 'https://kibu.family/#organization',
                name: 'Kibu',
                url: 'https://kibu.family/',
                logo: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/logos/kibu-logo.webp',
                sameAs: [
                    'https://www.instagram.com/kibu.family/',
                ],
                description: 'Kibu makes award-winning buildable, repairable and recyclable headphones for children.',
            },
            {
                '@type': 'WebSite',
                '@id': 'https://kibu.family/#website',
                name: 'Kibu',
                alternateName: 'Kibu Family',
                url: 'https://kibu.family/',
                publisher: { '@id': 'https://kibu.family/#organization' },
            },
        ],
    };

    return (
        <>
        <Helmet>
            <title>{searchMetadata.home.title}</title>
            <meta name="description" content={searchMetadata.home.description} />
            <meta property="og:title" content={searchMetadata.home.title} />
            <meta property="og:description" content={searchMetadata.home.description} />
            <meta property="og:url" content={canonicalUrl} />
            <meta name="twitter:title" content={searchMetadata.home.title} />
            <meta name="twitter:description" content={searchMetadata.home.description} />
            <link rel="canonical" href={canonicalUrl} />
            {SEO_REGIONS.map(({ code, hreflang }) => (
                <link
                    key={code}
                    rel="alternate"
                    hrefLang={hreflang}
                    href={toAbsoluteSiteUrl(getRegionalHomePath(code))}
                />
            ))}
            <link rel="alternate" hrefLang="x-default" href={toAbsoluteSiteUrl('/')} />
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
        <div className="flex flex-col">
            {/* Christmas Decorations */}
            {/* <ChristmasDecorations showSnowflakes={true} /> */}

            {/* Hero Section - Critical for LCP */}
            <div className="flex flex-col rounded-b-hero">

                <section className="relative w-full flex items-center justify-center overflow-hidden md:rounded-b-hero hero-section bg-green">
                    {/* Content - Moved above video to prioritize rendering */}
                    <div className="standard_container z-10 absolute inset-0 flex flex-col md:flex-row gap-8 items-center p-6 md:p-0">
                        <div className="flex flex-col h-full w-full md:w-2/3 justify-between md:justify-center md:gap-4 md:ml-12">
                            <AnimatePresence mode="popLayout" initial={false}>
                                <motion.div
                                    key={activeHeroIndex}
                                    className="flex flex-col gap-2 md:gap-4"
                                    // Forces framer's JS animation path; its WAAPI path flashes
                                    // the start opacity for one frame at the end in Chrome.
                                    onUpdate={noop}
                                    initial={{ opacity: 0 }}
                                    animate={{
                                        opacity: 1,
                                        transition: { duration: 0.4, delay: 0.4, ease: [0.4, 0, 0.2, 1] }
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: { duration: 0.3, delay: 0.1, ease: [0.4, 0, 0.2, 1] }
                                    }}
                                >
                                    {/* Title Section */}
                                    {currentImage.isNew && (
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="inline-flex items-center gap-2 bg-white/50 backdrop-blur-sm text-black px-4 py-2 rounded-full text-sm font-headline animate-pulse">
                                                <span className="w-2 h-2 bg-primary rounded-full animate-ping"></span>
                                                JUST LAUNCHED
                                            </div>
                                            <div className={`text-sm font-headline ${currentImage.accentColor || 'text-yellow-400'}`}>
                                                {currentImage.colorway}
                                            </div>
                                        </div>
                                    )}

                                    <h1 className={`text-4xl md:text-7xl font-headline hero-title ${currentImage.textColor || 'text-specialYellow'}`}>
                                        {currentImage.tagline1}
                                        <br />
                                        {currentImage.tagline2}
                                        {currentImage.tagline3 && (
                                            <>
                                                <br />
                                                <span className={currentImage.tagline3ClassName || 'text-4xl md:text-7xl'}>
                                                    {currentImage.tagline3}
                                                </span>
                                            </>
                                        )}
                                    </h1>
                                </motion.div>
                            </AnimatePresence>

                            {/* Description section */}
                            <AnimatePresence mode="popLayout" initial={false}>
                                <motion.div
                                    key={activeHeroIndex}
                                    className="flex flex-col gap-2 mt-auto md:mt-0"
                                    // Forces framer's JS animation path; its WAAPI path flashes
                                    // the start opacity for one frame at the end in Chrome.
                                    onUpdate={noop}
                                    initial={{ opacity: 0 }}
                                    animate={{
                                        opacity: 1,
                                        transition: { duration: 0.4, delay: 0.5, ease: [0.4, 0, 0.2, 1] }
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: { duration: 0.3, delay: 0, ease: [0.4, 0, 0.2, 1] }
                                    }}
                                >
                                    <p className={`text-lg display-block font-system w-3/4 hero-text leading-tight md:leading-normal ${currentImage.textColor || 'text-specialYellow'} hidden md:block`}>
                                        {currentImage.description || 'Join the headphone adventure where children build their own sustainable headphones, learning valuable skills while protecting their ears and our planet.'}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Persistent CTA — anchored to a fixed spot in the section so it
                        doesn't reflow with per-slide title/description height changes. */}
                    <div className="absolute inset-x-0 bottom-6 md:bottom-[14%] z-20 pointer-events-none">
                        <div className="standard_container flex px-6 md:px-0">
                            <div className="md:ml-12 pointer-events-auto">
                                <HeroCTA region={region} image={currentImage} />
                            </div>
                        </div>
                    </div>

                    {/* Hero Carousel */}
                    <HeroCarousel
                        images={heroImages}
                        onImageChange={handleImageChange}
                        snowflakeCount={snowCount}
                    />
                </section>

            </div>


            {/* Lazy load all non-critical content */}
            <Suspense fallback={<div className="h-20"></div>}>
                <PublicationLogoBanner
                    className="h-14 py-4 mt-1"
                />
                <div className='modules_wrapper mt-12'>
                    {/* Publication Logo Banner */}


                    {/* Black Friday Promo Section */}
                    {/* <BlackFridayPromo
                    imageUrl="https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/graphics/black-friday-background.webp"
                    promoCode="BF-ENGRAVING25"
                    heading="Black Friday Exclusive"
                    subheading="Free Headband Engraving with all orders"
                    startDate="2025-11-21"
                    endDate="2025-12-04T23:59:59"
                /> */}

                    <WelcomeVideo
                        youtubeVideoId="qhpXs82IaiQ"
                        backgroundVideoUrl="https://d1tg5gdu842vo5.cloudfront.net/site-assets/video/welcome_videos/kibu_background_video.webm"
                    />

                    {/* Santa Countdown Module */}
                    {/* <SantaCountdownModule
                    snowflakeCount={60}
                    christmasMessage="Merry Christmas!"
                    backgroundImage="https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/graphics/christmas_countdown_background.webp"
                /> */}

                    {/* <div className="banner-111 relative flex bg-specialRed items-center justify-center font-headline text-xl rounded-lg overflow-hidden mx-auto p-4" >
                    <img src="/assets/graphics/british_flag.webp" alt="British Flag" className="absolute inset-0 w-full h-full object-cover object-center opacity-30" />
                    <span className="text-white z-10">🇬🇧 Made in Britain 🇬🇧</span>
                </div> */}
                    {/* <BannerModule
                    // media={madeInBritain.media}
                    content={madeInBritain.content}
                    backgroundColor="bg-specialRed"
                    theme="dark"
                    button={false}
                    emoji="🇬🇧"
                /> */}

                    <LazyModule>
                        <KibuStoryModule />
                    </LazyModule>
                    <LazyModule>
                        <CompatibilityModule />
                    </LazyModule>

                    <LazyModule>
                        <UserGallery
                            items={galleryImages}
                            title="Kibu is loved!"
                            description="The #KibuHeadphones are loved by kids and parents alike. Share your photos and videos with us on Instagram and tag @kibu.family for a chance to be featured!"
                            ctaText="Instagram"
                            ctaLink="https://www.instagram.com/kibu.family/"
                            className=""
                        />
                    </LazyModule>

                    <LazyModule>
                        <AwardsShowcaseModule className="module_container" />
                    </LazyModule>
                    {/* <LazyModule>
                        <SpotlightSplitModule
                            media={awardWinning.media}
                            content={awardWinning.content}
                            backgroundColor="bg-[#7861FF]"
                            theme="dark"
                            className=''
                        />
                    </LazyModule> */}

                    <LazyModule>
                        <ReviewsModule
                            onViewAllClick={() => {
                                console.log('View all reviews clicked');
                            }}
                            className="mt-0"
                        />
                    </LazyModule>



                    <LazyModule>
                        {/* Bioplastic Material Showcase */}
                        <ScallopedShowcase
                            badge={{
                                emoji: '🌽',
                                text: ['MADE FROM', 'CORN'],
                                badgeColor: 'yellow-500'
                            }}
                            shapeType="circle"
                            backgroundColor="bg-[#577553]"
                            circleColor='#577553'
                        >
                            <h2 className="text-4xl md:text-5xl font-black text-teal-200 mb-2 mt-6 leading-tight">Made From Plants,</h2>
                            <div className="text-4xl md:text-5xl font-black mb-6 leading-tight" style={{
                                backgroundImage: 'linear-gradient(135deg,rgb(50, 223, 114) 0%,rgb(39, 221, 161) 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text'
                            }}>
                                Not Oil!
                            </div>
                            <p className="text-lg md:text-base text-teal-100 leading-relaxed mx-auto max-w-[550px] mb-4">
                                Over 70% of each pair of headphones is made from a recycled, plant-based plastic derived from plants like corn. It's tough, repairable, and circular!
                            </p>
                            {/* Bottom CTA - Inside content */}
                            <Button
                                to="/sustainability/"
                                text="Learn More"
                                variant="solid-green"
                                className="mb-6"
                            />
                        </ScallopedShowcase>
                    </LazyModule>

                    {/* <SpotlightSplitModule
                        media={buildSpotlight.media}
                        content={buildSpotlight.content}
                        backgroundColor="bg-kibuBlue"
                    /> */}

                    <LazyModule>
                        <ProductCollectionsModule
                            collectionCode="F39Jxkd4q"
                            className=""
                            showSubtitle={false}
                        />
                    </LazyModule>

                    <LazyModule>
                        <ComparisonModule
                            title="Why Choose Kibu?"
                            subtitle="See how Kibu headphones compare to standard children's headphones"
                        />
                    </LazyModule>

                    <LazyModule>
                        <SpotlightsModule
                            media={sustainabilitySpotlight.media}
                            content={sustainabilitySpotlight.content}
                            theme="dark"
                        />
                    </LazyModule>

                    <LazyModule>
                        <EnquiriesModule
                            fadeEffect={true}
                            investorFootnote="This contact form is for enquiries only and does not constitute an offer to sell, or a solicitation to buy, any shares or other securities."
                        />
                    </LazyModule>

                    <LazyModule>
                        <BlogModule
                            className="my-16"
                            showExcerpt={true}
                            showCategory={true}
                            maxPosts={6}
                        />
                    </LazyModule>
                </div>
            </Suspense>
        </div>
        </>
    );
};

export default Home; 
